/**
 * Landing Page
 * Authentication and onboarding for new users
 */

import { LegalModal } from '@components/ui';
import { SEO_LOGIN, SEO_REGISTER } from '@config/seoConfig';
import { useAuth } from '@contexts/AuthContext';
import { useToast } from '@contexts/ToastContext';
import { useFeatureFlags } from '@hooks/useFeatureFlags';
import { useSEO } from '@hooks/useSEO';
import { AUTH_OPERATION_FALLBACKS } from '@parlay-picker/shared/authMessages';
import { getInSeasonLeagues } from '@parlay-picker/shared/league';
import { type FormEvent, type ReactElement, useCallback, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AccountPauseModal } from './auth/AccountPauseModal';
import { AccountRecoveryModal } from './auth/AccountRecoveryModal';
import { AuthForm } from './auth/AuthForm';
import { AuthPageFooter } from './auth/AuthPageFooter';
import { AuthPageHeader } from './auth/AuthPageHeader';
import { AuthPageHero } from './auth/AuthPageHero';
import { HowItWorksSection } from './auth/HowItWorksSection';
import { OAuthLinkingModal } from './auth/OAuthLinkingModal';
import { OAuthRegisterModal } from './auth/OAuthRegisterModal';
import { ResetSentScreen } from './auth/ResetSentScreen';
import { VerificationSentScreen } from './auth/VerificationSentScreen';

type LegalDocType = 'terms' | 'privacy';

type AuthMode = 'login' | 'register' | 'forgot' | 'reset-sent' | 'verification-sent';

interface PasswordStrength {
  score: number;
  label: string;
  color: string;
}

export function AuthPage(): ReactElement {
  const { login, register, requestPasswordReset, oauthLogin, resendVerificationPublic } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { inviteOnly, oauthGoogle, oauthApple } = useFeatureFlags();

  useSEO(pathname === '/register' ? SEO_REGISTER : SEO_LOGIN);
  // Show which leagues are currently active (e.g. "NBA + NCAAB" in March)
  const activeLeaguesLabel = useMemo(() => {
    const active = getInSeasonLeagues();
    return active.join(' + ');
  }, []);
  // Invite code from URL params (magic link)
  const inviteCodeFromUrl = searchParams.get('invite') || '';
  const emailFromUrl = searchParams.get('email') || '';
  const hasInviteLink = !!(inviteCodeFromUrl && emailFromUrl);

  const [mode, setMode] = useState<AuthMode>(hasInviteLink ? 'register' : 'login');
  const [inviteCode, setInviteCode] = useState(inviteCodeFromUrl);
  const [email, setEmail] = useState(emailFromUrl);
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [devResetToken, setDevResetToken] = useState<string | null>(null);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [tosAccepted, setTosAccepted] = useState(false);
  const [showLegalModal, setShowLegalModal] = useState<LegalDocType | null>(null);
  const [viewedDocs, setViewedDocs] = useState({
    terms: false,
    privacy: false,
  });

  // OAuth state
  const [oauthLoading, setOauthLoading] = useState<'google' | 'apple' | null>(null);
  const [googleFallback, setGoogleFallback] = useState(false);
  const googleBtnRef = useRef<HTMLDivElement>(null);
  const [oauthLinkingData, setOauthLinkingData] = useState<{
    provider: string;
    idToken: string;
    email: string;
  } | null>(null);
  const [linkPassword, setLinkPassword] = useState('');
  const [linkError, setLinkError] = useState('');
  const [linkLoading, setLinkLoading] = useState(false);
  const [oauthRegData, setOauthRegData] = useState<{
    provider: string;
    idToken: string;
    email: string;
    firstName?: string;
    lastName?: string;
  } | null>(null);
  const [oauthDisplayName, setOauthDisplayName] = useState('');
  const [oauthTosAccepted, setOauthTosAccepted] = useState(false);
  const [oauthRegLoading, setOauthRegLoading] = useState(false);
  const [oauthRegError, setOauthRegError] = useState('');
  const [oauthInviteCode, setOauthInviteCode] = useState('');
  const [recoveryData, setRecoveryData] = useState<{
    email: string;
    deletedAt: string;
    permanentDeletionDate: string;
    /** If set, recovery came from OAuth and we can re-auth via provider */
    oauthProvider?: string;
    oauthIdToken?: string;
  } | null>(null);
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoveryError, setRecoveryError] = useState('');
  const [pauseData, setPauseData] = useState<{
    email: string;
    oauthProvider?: string;
    oauthIdToken?: string;
  } | null>(null);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [resumeError, setResumeError] = useState('');

  // Handle opening legal modal and mark as viewed when closed
  const openLegalDoc = (type: LegalDocType): void => {
    setShowLegalModal(type);
  };

  const closeLegalModal = (): void => {
    if (showLegalModal) {
      setViewedDocs((prev) => ({ ...prev, [showLegalModal]: true }));
    }
    setShowLegalModal(null);
  };

  // Can only accept TOS after viewing both documents
  const canAcceptTos = viewedDocs.terms && viewedDocs.privacy;

  // Password strength calculation
  const getPasswordStrength = (pwd: string): PasswordStrength => {
    if (!pwd) return { score: 0, label: '', color: '' };
    let score = 0;
    if (pwd.length >= 6) score++;
    if (pwd.length >= 8) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;

    if (score <= 1) return { score: 1, label: 'Weak', color: 'bg-theme-danger' };
    if (score <= 2) return { score: 2, label: 'Fair', color: 'bg-theme-warning' };
    if (score <= 3) return { score: 3, label: 'Good', color: 'bg-theme-primary' };
    return { score: 4, label: 'Strong', color: 'bg-theme-success' };
  };

  const passwordStrength = mode === 'register' ? getPasswordStrength(password) : null;

  // Email validation
  const isValidEmail = (em: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em);
  const showEmailError = mode === 'register' && email && !isValidEmail(email);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (mode === 'login') {
        await login(email, password, rememberMe);
        navigate('/');
      } else if (mode === 'register') {
        const result = await register(email, password, {
          firstName,
          lastName,
          displayName,
          tosAccepted,
          ...(inviteOnly && inviteCode && { inviteCode }),
        });
        if ('type' in result && result.type === 'verification_required') {
          setVerificationEmail(result.email);
          setMode('verification-sent');
        } else {
          navigate('/');
        }
      } else if (mode === 'forgot') {
        const result = await requestPasswordReset(email);
        setDevResetToken(result.devToken || null);
        setMode('reset-sent');
      }
    } catch (err) {
      const message = (err as Error).message || 'An error occurred';
      if (message === 'account_pending_deletion') {
        setRecoveryData({
          email,
          deletedAt: '',
          permanentDeletionDate: '',
        });
      } else if (message === 'account_paused') {
        setPauseData({ email });
      } else if (message.startsWith('email_verification_required:')) {
        const verifyEmail = message.split(':').slice(1).join(':');
        setVerificationEmail(verifyEmail);
        setMode('verification-sent');
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (newMode: AuthMode): void => {
    setMode(newMode);
    setError('');
    setDevResetToken(null);
    setTosAccepted(false);
    setViewedDocs({ terms: false, privacy: false });
    setVerificationEmail('');
    setResendSuccess(false);
  };

  // OAuth: handle the response from oauthLogin
  const handleOAuthResult = useCallback(
    async (provider: string, idToken: string, firstName?: string, lastName?: string) => {
      try {
        const result = await oauthLogin(provider, idToken, {
          firstName,
          lastName,
        });

        if (result.type === 'success') {
          navigate('/');
          return;
        }

        if (result.type === 'linking_required') {
          setOauthLinkingData({ provider, idToken, email: result.email || '' });
          return;
        }

        if (result.type === 'registration_required') {
          setOauthRegData({
            provider,
            idToken,
            email: result.email || '',
            firstName: result.firstName,
            lastName: result.lastName,
          });
          if (result.firstName && result.lastName) {
            setOauthDisplayName(`${result.firstName}${result.lastName}`);
          }
          return;
        }

        if (result.type === 'account_pending_deletion') {
          setRecoveryData({
            email: result.email || '',
            deletedAt: result.deletedAt || '',
            permanentDeletionDate: result.permanentDeletionDate || '',
            oauthProvider: provider,
            oauthIdToken: idToken,
          });
          return;
        }

        if (result.type === 'account_paused') {
          setPauseData({
            email: result.email || '',
            oauthProvider: provider,
            oauthIdToken: idToken,
          });
          return;
        }
      } catch (err) {
        setError((err as Error).message || AUTH_OPERATION_FALLBACKS.oauthLogin);
      } finally {
        setOauthLoading(null);
      }
    },
    [oauthLogin, navigate],
  );

  // OAuth: Google Sign-In
  const handleGoogleSignIn = useCallback(() => {
    setOauthLoading('google');
    setError('');

    const google = (window as unknown as Record<string, unknown>).google as
      | {
          accounts: {
            id: {
              initialize: (cfg: Record<string, unknown>) => void;
              prompt: (cb?: (notification: { isNotDisplayed: () => boolean }) => void) => void;
              renderButton: (parent: HTMLElement, cfg: Record<string, unknown>) => void;
            };
          };
        }
      | undefined;

    if (!google?.accounts?.id) {
      setError('Google Sign-In is not available. Please try again later.');
      setOauthLoading(null);
      return;
    }

    google.accounts.id.initialize({
      client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID || '',
      callback: (response: { credential?: string }) => {
        if (response.credential) {
          handleOAuthResult('google', response.credential);
        } else {
          setError('Google Sign-In was cancelled');
          setOauthLoading(null);
        }
      },
      auto_select: false,
      context: 'signin',
      itp_support: true,
      use_fedcm_for_prompt: true,
    });

    google.accounts.id.prompt(
      (notification: { isNotDisplayed: () => boolean; getNotDisplayedReason?: () => string }) => {
        if (notification.isNotDisplayed()) {
          const reason = notification.getNotDisplayedReason?.() ?? 'unknown';
          // One Tap couldn't display (cookies blocked, cooldown, incognito, etc.)
          // Fall back to Google's rendered sign-in button
          setOauthLoading(null);
          if (googleBtnRef.current) {
            google.accounts.id.renderButton(googleBtnRef.current, {
              type: 'standard',
              theme: 'outline',
              size: 'large',
              text: 'signin_with',
              width: googleBtnRef.current.offsetWidth,
            });
            setGoogleFallback(true);
          } else {
            setError(
              `Google Sign-In couldn't load. Reason: ${reason}. Try a different browser or disable ad blockers.`,
            );
          }
        }
      },
    );
  }, [handleOAuthResult]);

  // OAuth: Apple Sign-In
  const handleAppleSignIn = useCallback(async () => {
    setOauthLoading('apple');
    setError('');

    const AppleID = (window as unknown as Record<string, unknown>).AppleID as
      | {
          auth: {
            init: (cfg: Record<string, unknown>) => void;
            signIn: () => Promise<{
              authorization: { id_token: string };
              user?: { name?: { firstName?: string; lastName?: string } };
            }>;
          };
        }
      | undefined;

    if (!AppleID?.auth) {
      setError('Apple Sign-In is not available. Please try again later.');
      setOauthLoading(null);
      return;
    }

    try {
      AppleID.auth.init({
        clientId: import.meta.env.VITE_APPLE_WEB_CLIENT_ID || '',
        scope: 'name email',
        redirectURI: `${window.location.origin}/auth/apple/callback`,
        usePopup: true,
      });

      const response = await AppleID.auth.signIn();
      const idToken = response.authorization.id_token;
      const appleFirstName = response.user?.name?.firstName;
      const appleLastName = response.user?.name?.lastName;

      await handleOAuthResult('apple', idToken, appleFirstName, appleLastName);
    } catch {
      setError('Apple Sign-In was cancelled');
      setOauthLoading(null);
    }
  }, [handleOAuthResult]);

  // OAuth: Complete linking with password
  const handleLinkAccount = async () => {
    if (!oauthLinkingData || !linkPassword) return;
    setLinkLoading(true);
    setLinkError('');

    try {
      const result = await oauthLogin(oauthLinkingData.provider, oauthLinkingData.idToken, {
        linkPassword,
      });

      if (result.type === 'success') {
        setOauthLinkingData(null);
        setLinkPassword('');
        navigate('/');
      } else {
        setLinkError('Failed to link account. Please check your password.');
      }
    } catch (err) {
      setLinkError((err as Error).message || 'Failed to link account');
    } finally {
      setLinkLoading(false);
    }
  };

  // OAuth: Complete registration with display name + TOS
  const handleOAuthRegister = async () => {
    if (!oauthRegData || !oauthDisplayName || !oauthTosAccepted) return;
    setOauthRegLoading(true);
    setOauthRegError('');

    try {
      const result = await oauthLogin(oauthRegData.provider, oauthRegData.idToken, {
        displayName: oauthDisplayName,
        tosAccepted: true,
        firstName: oauthRegData.firstName,
        lastName: oauthRegData.lastName,
        ...(inviteOnly && oauthInviteCode && { inviteCode: oauthInviteCode }),
      });

      if (result.type === 'success') {
        setOauthRegData(null);
        setOauthDisplayName('');
        setOauthTosAccepted(false);
        navigate('/');
      } else {
        setOauthRegError(result.email ? `Error: ${result.email}` : 'Registration failed');
      }
    } catch (err) {
      setOauthRegError((err as Error).message || 'Registration failed');
    } finally {
      setOauthRegLoading(false);
    }
  };

  // Resend verification email handler
  const handleResendVerification = async (): Promise<void> => {
    setResendLoading(true);
    setResendSuccess(false);
    try {
      await resendVerificationPublic(verificationEmail);
      setResendSuccess(true);
    } catch {
      setError(AUTH_OPERATION_FALLBACKS.resendVerification);
    } finally {
      setResendLoading(false);
    }
  };

  // Email verification required screen
  if (mode === 'verification-sent') {
    return (
      <VerificationSentScreen
        verificationEmail={verificationEmail}
        resendSuccess={resendSuccess}
        resendLoading={resendLoading}
        onResend={handleResendVerification}
        onBackToSignIn={() => switchMode('login')}
      />
    );
  }

  // Reset sent confirmation screen
  if (mode === 'reset-sent') {
    return (
      <ResetSentScreen
        email={email}
        devResetToken={devResetToken}
        onBackToSignIn={() => switchMode('login')}
      />
    );
  }

  return (
    <div
      data-testid="landing-page"
      className="min-h-dvh bg-theme-bg flex flex-col relative overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-200 h-150 bg-theme-primary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-100 h-100 bg-theme-secondary/5 rounded-full blur-3xl pointer-events-none" />

      <AuthPageHeader activeLeaguesLabel={activeLeaguesLabel} />

      <main className="flex-1 flex items-start sm:items-center justify-center px-4 sm:px-6 pb-6 sm:pb-12 relative z-10 -mt-4">
        <div className="w-full max-w-5xl">
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-12 lg:gap-16 items-center">
            <AuthPageHero />

            {/* Auth Form column */}
            <div>
              <AuthForm
                mode={mode}
                email={email}
                password={password}
                firstName={firstName}
                lastName={lastName}
                displayName={displayName}
                inviteCode={inviteCode}
                inviteCodeFromUrl={inviteCodeFromUrl}
                hasInviteLink={hasInviteLink}
                rememberMe={rememberMe}
                tosAccepted={tosAccepted}
                error={error}
                loading={loading}
                inviteOnly={inviteOnly}
                oauthGoogle={oauthGoogle}
                oauthApple={oauthApple}
                oauthLoading={oauthLoading}
                googleFallback={googleFallback}
                googleBtnRef={googleBtnRef}
                viewedDocs={viewedDocs}
                canAcceptTos={canAcceptTos}
                passwordStrength={passwordStrength}
                showEmailError={showEmailError}
                onEmailChange={setEmail}
                onPasswordChange={setPassword}
                onFirstNameChange={setFirstName}
                onLastNameChange={setLastName}
                onDisplayNameChange={setDisplayName}
                onInviteCodeChange={setInviteCode}
                onRememberMeChange={setRememberMe}
                onTosChange={setTosAccepted}
                onSubmit={handleSubmit}
                onSwitchMode={switchMode}
                onGoogleSignIn={handleGoogleSignIn}
                onAppleSignIn={handleAppleSignIn}
                onOpenLegalDoc={openLegalDoc}
              />
            </div>
          </div>

          <HowItWorksSection />
        </div>
      </main>

      <AuthPageFooter onOpenLegalDoc={openLegalDoc} />

      {/* Legal Document Modal */}
      {showLegalModal && <LegalModal type={showLegalModal} onClose={closeLegalModal} />}

      {/* OAuth Linking Modal — existing email, needs password */}
      {oauthLinkingData && (
        <OAuthLinkingModal
          linkingData={oauthLinkingData}
          linkPassword={linkPassword}
          linkError={linkError}
          linkLoading={linkLoading}
          onPasswordChange={setLinkPassword}
          onLink={handleLinkAccount}
          onCancel={() => {
            setOauthLinkingData(null);
            setLinkPassword('');
            setLinkError('');
          }}
        />
      )}

      {/* OAuth Registration Modal — new user, needs display name + TOS */}
      {oauthRegData && (
        <OAuthRegisterModal
          regData={oauthRegData}
          displayName={oauthDisplayName}
          tosAccepted={oauthTosAccepted}
          regLoading={oauthRegLoading}
          regError={oauthRegError}
          inviteOnly={inviteOnly}
          inviteCode={oauthInviteCode}
          onDisplayNameChange={setOauthDisplayName}
          onTosChange={setOauthTosAccepted}
          onInviteCodeChange={setOauthInviteCode}
          onRegister={handleOAuthRegister}
          onCancel={() => {
            setOauthRegData(null);
            setOauthDisplayName('');
            setOauthTosAccepted(false);
            setOauthRegError('');
          }}
          onOpenLegalDoc={openLegalDoc}
        />
      )}

      {/* Account Pause Resume Modal */}
      {pauseData && (
        <AccountPauseModal
          pauseData={pauseData}
          password={password}
          rememberMe={rememberMe}
          resumeLoading={resumeLoading}
          resumeError={resumeError}
          onResumeLoadingChange={setResumeLoading}
          onResumeErrorChange={setResumeError}
          onPauseDataClear={() => setPauseData(null)}
          onNavigateHome={() => navigate('/')}
          onLogin={login}
          onShowToast={showToast}
        />
      )}

      {/* Account Recovery Modal */}
      {recoveryData && (
        <AccountRecoveryModal
          recoveryData={recoveryData}
          password={password}
          rememberMe={rememberMe}
          recoveryLoading={recoveryLoading}
          recoveryError={recoveryError}
          onRecoveryLoadingChange={setRecoveryLoading}
          onRecoveryErrorChange={setRecoveryError}
          onRecoveryDataClear={() => setRecoveryData(null)}
          onNavigateHome={() => navigate('/')}
          onLogin={login}
          onShowToast={showToast}
        />
      )}
    </div>
  );
}
